import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { HttpHeaders, HttpErrorResponse, HttpResponse, HttpParams, HttpInterceptor } from '@angular/common/http';
import { IHttpRequestService } from './ihttp-request.service';
import { UtilService } from '../util/util.service';
import { LogService } from '../log/log.service';

import { Observable, Subject, of, throwError } from 'rxjs';
import { catchError, retry, map, take } from 'rxjs/operators';

import { Error } from '../model/error.model';
import { HttpRequest } from './http-request.model';
import { HttpRequestOptions } from './http-request-options.model';
import { NameValue } from '../model/name-value.model';
import { getSafe } from '../util/get-safe';

@Injectable()
export class HttpRequestService {
  private _http: HttpClient;
  private _utilService: UtilService;
  private _logService: LogService;
  private _requests: Array<HttpRequest>;
  private _defaultCacheSeconds: number;
  private _instanceId: number;

  public constructor(http: HttpClient, utilService: UtilService, logService: LogService) {
    this._http = http;
    this._utilService = utilService;
    this._logService = logService;

    this._requests = new Array<HttpRequest>();
    this._defaultCacheSeconds = 30;
    this._instanceId = Math.round(Math.random() * 1000);
  }

  public getRequestItems = (): Array<HttpRequest> => {
    this.pruneRequests();

    return this._requests;
  };

  public getDefaultCacheSeconds = (): number => {
    return this._defaultCacheSeconds;
  };

  public getRequest = (url: string, requestOptions: HttpRequestOptions): Promise<any> => {
    this.pruneRequests();
    this._logService.log(
      'Http Request Service Instance Id ' + this._instanceId.toString(),
      'Requests Being Tracked ' + this._requests.length.toString()
    );

    let requestCached = this.getRequestCached(url);
    if (requestOptions.DeleteCache == true) {
      if (requestCached != null) {
        requestCached.CacheExpires = new Date(1900, 1, 1); //change it to be expired

        requestCached = null;
      }
    }

    if (requestCached != null) {
      this._logService.log('http request has existing cached data.  That data is now being returned.', url);
      if (requestOptions.SlidingCache == true) {
        this._logService.log(
          'http request has a sliding cached version.  The expiration will slide forward ' +
            requestCached.SlidingCacheSeconds.toString() +
            ' seconds.',
          url
        );
        requestCached.CacheExpires = new Date(new Date().getTime() + requestCached.SlidingCacheSeconds * 1000);
      } else {
        this._logService.log('http request has a non-sliding cached version.', url);
      }
      return Promise.resolve(requestCached.ResponseData);
      //if sliding cache increment the cache expires
    } else {
      let requestInProgress = this.getRequestInProgress(url);
      if (requestInProgress != null) {
        this._logService.log('http request already in progress.  Request will be queued until the in progress request responds.', url);
        return requestInProgress.ResponsePromise;
      } else {
        let request = new HttpRequest();
        if (requestOptions.EnableCaching == true) {
          request.EnableCaching = true;
          //new Date(new Date().getTime() + (30 * 1000)); //expire cache in 30 seconds
          if (requestOptions.CacheSeconds > 0) {
            request.CacheExpires = new Date(new Date().getTime() + requestOptions.CacheSeconds * 1000);
          } else {
            request.CacheExpires = new Date(new Date().getTime() + this._defaultCacheSeconds * 1000);
          }

          if (requestOptions.SlidingCache == true) {
            request.SlidingCache = true;
            if (requestOptions.SlidingCacheSeconds > 0) {
              request.SlidingCacheSeconds = requestOptions.SlidingCacheSeconds;
            } else {
              request.SlidingCacheSeconds = this._defaultCacheSeconds;
            }
          }
        } else {
          this._logService.log('http request being made will NOT be cached', url);
        }
        request.InProgress = true;
        request.Url = url;
        request.PrimaryRequest = true;

        let hdrs = this.getHttpHeadersFromNameValues(requestOptions.Headers);

        this._logService.log('LIVE http request being made to remote resource', url);
        let responsePromise = this._http
          .get(url, { headers: hdrs })
          .toPromise()
          .then(
            (res: Response) => {
              let data = this.extractData(res);
              for (let request of this._requests) {
                if (request.Url == url) {
                  request.InProgress == false;
                  if (request.PrimaryRequest == true && request.EnableCaching == true) {
                    //set cached data on the primary request only not any queued requests
                    this._logService.log('http request will be cached until ' + request.CacheExpires.toString(), url);
                    request.ResponseData = data;
                  }
                  request.Completed = true;

                  this._logService.log('http queued request is about to be fulfilled', url);
                }
              }
              this._logService.log('http request returned data', url);
              return Promise.resolve(data);
            },
            (errRes: any) => {
              let err = this.extractError(errRes);
              for (let request of this._requests) {
                if (request.Url == url) {
                  request.InProgress == false;
                  request.Completed = true;
                }
              }
              let errorUrl = '';
              if (this._utilService.isNotNullEmptyOrWhitespace(url)) {
                errorUrl = url;
              }

              if (this._utilService.isNotNullOrUndefined(err) && this._utilService.isNotNullOrUndefined(err.ExceptionMessage)) {
                this._logService.log('api request returned error', errorUrl + ' ' + err.ExceptionMessage);
              } else {
                this._logService.log('api request returned error', errorUrl + ' ' + errRes.toString());
              }

              return Promise.reject(errRes);
            }
          );

        request.ResponsePromise = responsePromise;
        this._utilService.arrayAddEntryToBeginning(this._requests, request);
        return responsePromise;
      }
    }
  };

  private extractData = (res: Response) => {
    //data now comes in as json
    let data: any = res; //null;
    if (getSafe(() => res.status) == 204) {
      //this is a no content response A VOID
    } else {
      //data = res.json();
      this._utilService.convertJsonUtcServerDateStringsToDates(data);
    }

    return data; //body.data || {};
  };

  private extractError = (error: any): Error => {
    let err = new Error();
    err.Message = 'Server error.';

    err.ExceptionMessage = error.statusText;
    err.Message = error.message;
    return err;
  };

  private getRequestInProgress = (url: string): HttpRequest => {
    let requestInProgress: HttpRequest = null;
    for (let request of this._requests) {
      if (
        request.InProgress == true &&
        request.Completed == false &&
        this._utilService.toLowerTrim(request.Url) == this._utilService.toLowerTrim(url)
      ) {
        requestInProgress = request;
        break;
      }
    }

    return requestInProgress;
  };

  private getRequestCached = (url: string): HttpRequest => {
    let requestCached: HttpRequest = null;

    for (let request of this._requests) {
      if (
        request.Completed == true &&
        this._utilService.toLowerTrim(request.Url) == this._utilService.toLowerTrim(url) &&
        request.PrimaryRequest == true
      ) {
        if (request.EnableCaching == true) {
          let currentDate = new Date();
          if (this.isCacheRequestExpired(request) == false) {
            requestCached = request;
            break;
          } else {
            this._logService.log('http request has a cached item but it has expired.', url);
            this.pruneRequests();
            break;
          }
        }
      }
    }

    return requestCached;
  };

  private isCacheRequestExpired = (request: HttpRequest): boolean => {
    let currentDate = new Date();

    if (currentDate < request.CacheExpires) {
      return false;
    } else {
      return true;
    }
  };

  private pruneRequests = (): Promise<void> => {
    return new Promise<void>(() => {
      for (let request of this._requests) {
        if (request.Completed == true) {
          request.InProgress = false;
        }
        if (request.Completed && request.EnableCaching == false) {
          this.removeRequestItem(request);
          this._logService.log('http request has been pruned because its completed and does not require caching.', request.Url);
        }

        if (request.Completed && request.EnableCaching == true) {
          if (this.isCacheRequestExpired(request) == true) {
            this.removeRequestItem(request);
            this._logService.log('http request has been pruned because its cache expired.', request.Url);
          }
        }
      }
    });
  };

  public removeRequestItem = (request: HttpRequest): void => {
    this._utilService.arrayRemoveEntry(this._requests, request);
    this._logService.log('http request item has been removed from the tracking list.', request.Url);
  };

  public deleteCache = (url: string): void => {
    let requestInProgress: HttpRequest = null;
    for (let request of this._requests) {
      if (this._utilService.toLowerTrim(request.Url) == this._utilService.toLowerTrim(url)) {
        request.SlidingCache = false;
        request.EnableCaching = false;
        request.ResponseData = null;
        this._logService.log('http request item has been flagged to no longer enable its cache.', request.Url);
      }
    }
    this.pruneRequests();
  };

  public deleteCacheContains = (urlContains: string): void => {
    for (let request of this._requests) {
      if (this._utilService.containsAnyCase(request.Url, urlContains)) {
        this.deleteCache(request.Url);
      }
    }
  };

  //other http request types post put delete
  public get = (url: string, requestOptions?: HttpRequestOptions): Promise<any> => {
    if (this._utilService.isNullOrUndefined(requestOptions)) {
      requestOptions = this.getDefaultRequestOptions();
    }
    let hdrs = this.getHttpHeadersFromNameValues(requestOptions.Headers);
    return this._http.get(url, { headers: hdrs }).toPromise();
  };

  public getObservable = (url: string, requestOptions?: HttpRequestOptions): Observable<any> => {
    if (this._utilService.isNullOrUndefined(requestOptions)) {
      requestOptions = this.getDefaultRequestOptions();
    }
    let hdrs = this.getHttpHeadersFromNameValues(requestOptions.Headers);
    return this._http.get(url, { headers: hdrs });
  };

  public post = (url: string, body: any, requestOptions?: HttpRequestOptions): Promise<any> => {
    if (this._utilService.isNullOrUndefined(requestOptions)) {
      requestOptions = this.getDefaultRequestOptions();
    }
    let hdrs = this.getHttpHeadersFromNameValues(requestOptions.Headers);
    return this._http.post(url, body, { headers: hdrs }).toPromise();
  };

  public postTextReturn = (url: string, body: any, requestOptions?: HttpRequestOptions): Promise<any> => {
    if (this._utilService.isNullOrUndefined(requestOptions)) {
      requestOptions = this.getDefaultRequestOptions();
    }
    let hdrs = this.getHttpHeadersFromNameValues(requestOptions.Headers);
    return this._http.post(url, body, { headers: hdrs, responseType: 'text' }).toPromise();
  };

  public put = (url: string, body: any, requestOptions?: HttpRequestOptions): Promise<any> => {
    if (this._utilService.isNullOrUndefined(requestOptions)) {
      requestOptions = this.getDefaultRequestOptions();
    }
    let hdrs = this.getHttpHeadersFromNameValues(requestOptions.Headers);
    return this._http.put(url, body, { headers: hdrs }).toPromise();
  };

  public delete = (url: string, requestOptions?: HttpRequestOptions): Promise<any> => {
    if (this._utilService.isNullOrUndefined(requestOptions)) {
      requestOptions = this.getDefaultRequestOptions();
    }
    let hdrs = this.getHttpHeadersFromNameValues(requestOptions.Headers);
    return this._http.delete(url, { headers: hdrs }).toPromise();
  };

  public deleteData = (url: string, body: any, requestOptions?: HttpRequestOptions): Promise<any> => {
    if (this._utilService.isNullOrUndefined(requestOptions)) {
      requestOptions = this.getDefaultRequestOptions();
    }
    let hdrs = this.getHttpHeadersFromNameValues(requestOptions.Headers);
    return this._http.request('delete', url, { body: body, headers: hdrs }).toPromise();
  };

  public getDefaultRequestOptions = (): HttpRequestOptions => {
    let reqOptions = new HttpRequestOptions();
    reqOptions.Headers = new Array<NameValue>();

    return reqOptions;
  };

  private getHttpHeadersFromNameValues = (nameValues: Array<NameValue>): HttpHeaders => {
    let currentHeaders = new HttpHeaders();
    for (let nv of nameValues) {
      currentHeaders = currentHeaders.set(nv.name, nv.value);
    }

    return currentHeaders;
  };

  public getFile = (url: string, requestOptions?: HttpRequestOptions): Observable<Blob> => {
    if (this._utilService.isNullOrUndefined(requestOptions)) {
      requestOptions = this.getDefaultRequestOptions();
    }
    let hdrs = this.getHttpHeadersFromNameValues(requestOptions.Headers);

    return this._http.get(url, { headers: hdrs, responseType: 'blob' });
  };
}
